import { type TboAuditOutcome } from '@utazzo/shared';
import { UzBadge } from '@utazzo/ui';
import React from 'react';

/**
 * Shared presentation helpers for the TBO audit viewer - kept in one place so
 * the list grid and the detail drawer render outcomes, timestamps and byte
 * counts identically.
 */

type BadgeTone = NonNullable<React.ComponentProps<typeof UzBadge>['tone']>;

const OUTCOME_TONE: Record<TboAuditOutcome, BadgeTone> = {
  SUCCESS: 'success',
  NO_RESULTS: 'secondary',
  TBO_ERROR: 'destructive',
  HTTP_ERROR: 'destructive',
  TIMEOUT: 'destructive',
};

const OUTCOME_LABEL: Record<TboAuditOutcome, string> = {
  SUCCESS: 'Success',
  NO_RESULTS: 'No results',
  TBO_ERROR: 'TBO error',
  HTTP_ERROR: 'HTTP error',
  TIMEOUT: 'Timeout',
};

/** Colored pill for a call outcome; renders a neutral dash for a null outcome. */
export const OutcomeBadge = ({
  outcome,
}: {
  outcome: TboAuditOutcome | null;
}): React.JSX.Element => {
  if (!outcome) return <span className="text-zinc-400">-</span>;
  return (
    <UzBadge tone={OUTCOME_TONE[outcome]} pill>
      {OUTCOME_LABEL[outcome]}
    </UzBadge>
  );
};

/** DD-MM-YYYY HH:mm in the viewer's local timezone. Falls back to the raw ISO. */
export const formatDateTimeDMY = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

/** Human-readable byte count (e.g. "12.3 KB"); returns '-' for null. */
export const formatBytes = (bytes: number | null): string => {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

/** UUIDs are long; show a short head for scannability while keeping a title. */
export const shortId = (id: string | null): string => (id ? `${id.slice(0, 8)}…` : '-');
